import React from 'react';
import SEOHead from '../components/SEOHead';
import { getCurrentDate } from '../utils/dateUtils';
import './Disclaimer.css';

const Disclaimer = () => {
  return (
    <>
      <SEOHead 
        title="Disclaimer - YT DOWN | Legal Information & Copyright Notice"
        description="Important legal information about using YT DOWN video downloader. Copyright compliance, user responsibilities, service limitations, and legal disclaimers for YouTube and TikTok downloads."
        keywords="disclaimer, legal information, copyright notice, yt down disclaimer, youtube downloader legal, tiktok downloader legal, user responsibility"
        url="https://digimac.pro/disclaimer"
      />
      <section id="disclaimer" className="disclaimer-section">
      <div className="disclaimer-container">
        <div className="disclaimer-hero">
          <h1 className="disclaimer-title">Disclaimer</h1>
          <p className="disclaimer-subtitle">Important Legal Information About Our YouTube & TikTok Video Downloader Service</p>
          <p className="last-updated">Last Updated: {getCurrentDate()}</p>
        </div>

        <div className="disclaimer-content">
          <DisclaimerContent />
        </div>
      </div>
    </section>
    </>
  );
};

const DisclaimerContent = () => (
  <>
    <div className="disclaimer-block intro-block">
      <h2>General Disclaimer</h2>
      <p>
        Welcome to YT DOWN, a free online YouTube and TikTok video downloader tool. This disclaimer outlines important legal information about using our service. By accessing or using our website, you acknowledge that you have read, understood, and agree to be bound by this disclaimer.
      </p>
      <p>
        <strong>Our Service:</strong> We provide an online tool that allows users to download publicly available videos from YouTube and TikTok by simply pasting a video URL. Our platform processes the URL and provides download links in various quality formats.
      </p>
      <p>
        <strong>No Affiliation:</strong> YT DOWN is an independent service and is NOT affiliated with, endorsed by, sponsored by, or partnered with YouTube, TikTok, Google LLC, ByteDance Ltd., or any other third-party platform or company. All trademarks, logos, and brand names mentioned on this website are the property of their respective owners.
      </p>
    </div>

    <div className="disclaimer-block highlight-block">
      <h2>No Ownership of Content</h2>
      <p>
        <strong>We Do Not Host or Store Videos:</strong> YT DOWN does not host, store, upload, or own any video content. We do not maintain any video files on our servers. Our service simply provides a technical tool that processes publicly available video URLs.
      </p>
      <p>
        <strong>Copyright Ownership:</strong> All videos downloaded through our service remain the intellectual property of their original creators and copyright holders. We do not claim any ownership rights over any content accessed or downloaded through our platform.
      </p>
      <p>
        <strong>Public Content Only:</strong> Our service only works with publicly accessible videos. We do not provide access to private, restricted, or age-restricted content.
      </p>
    </div>

    <div className="disclaimer-block warning-block">
      <h2>User Responsibility & Copyright Compliance</h2>
      <p>
        <strong>You Are Fully Responsible:</strong> Users are solely and entirely responsible for how they use the downloaded content. By using our service, you agree to:
      </p>
      <ul>
        <li>Download videos only for personal, non-commercial use</li>
        <li>Respect copyright laws and intellectual property rights</li>
        <li>Comply with YouTube's Terms of Service and TikTok's Terms of Service</li>
        <li>Obtain necessary permissions from content creators when required</li>
        <li>Not redistribute, republish, or monetize downloaded content without authorization</li>
      </ul>
      <p>
        <strong>Copyright Infringement:</strong> Downloading copyrighted content without permission may violate copyright laws in your jurisdiction. We strongly advise users to download only content they have the right to download or content that is explicitly marked as free to use.
      </p>
      <p>
        <strong>No Liability for Misuse:</strong> YT DOWN is not responsible or liable for any copyright infringement, illegal downloading, or misuse of content by users. Any legal consequences arising from improper use of downloaded content are the sole responsibility of the user.
      </p>
    </div>

    <div className="disclaimer-block">
      <h2>Accuracy & Service Limitations</h2>
      <p>
        <strong>Best Effort Service:</strong> While we strive to provide accurate, reliable, and high-quality service, we do not guarantee:
      </p>
      <ul>
        <li>100% uptime or uninterrupted service availability</li>
        <li>Accuracy of download links or video quality</li>
        <li>Compatibility with all video URLs or formats</li>
        <li>Error-free operation at all times</li>
      </ul>
      <p>
        <strong>Technical Limitations:</strong> Our service may experience technical issues, API limitations, server downtime, or temporary interruptions due to factors beyond our control, including but not limited to:
      </p>
      <ul>
        <li>Changes to YouTube or TikTok APIs and policies</li>
        <li>Network connectivity issues</li>
        <li>Third-party service provider limitations</li>
        <li>Maintenance and updates</li>
      </ul>
      <p>
        <strong>No Warranty:</strong> The service is provided "AS IS" and "AS AVAILABLE" without any warranties of any kind, either express or implied.
      </p>
    </div>

    <div className="disclaimer-block">
      <h2>External Links & Third-Party Websites</h2>
      <p>
        Our website may contain links to external websites or third-party services for informational purposes. Please note:
      </p>
      <ul>
        <li>We are not responsible for the content, privacy practices, or activities of external websites</li>
        <li>We do not endorse or guarantee the accuracy of information on third-party sites</li>
        <li>Visiting external links is at your own risk</li>
        <li>We recommend reviewing the terms and privacy policies of any external websites you visit</li>
      </ul>
    </div>

    <div className="disclaimer-block">
      <h2>No Legal Advice</h2>
      <p>
        <strong>Information Only:</strong> The information provided on this website, including this disclaimer, is for general informational purposes only and should not be construed as legal advice.
      </p>
      <p>
        <strong>Consult Legal Professionals:</strong> If you have questions about copyright law, intellectual property rights, or the legality of downloading specific content, please consult with a qualified legal professional in your jurisdiction.
      </p>
      <p>
        We make no representations or warranties regarding the legal implications of using our service in any specific jurisdiction.
      </p>
    </div>

    <div className="disclaimer-block highlight-block">
      <h2>Limitation of Liability</h2>
      <p>
        <strong>No Liability:</strong> To the fullest extent permitted by law, YT DOWN, its owners, operators, employees, and affiliates shall not be liable for:
      </p>
      <ul>
        <li>Any direct, indirect, incidental, consequential, or punitive damages</li>
        <li>Loss of data, revenue, or profits</li>
        <li>Service interruptions or technical errors</li>
        <li>Damages resulting from use or inability to use our service</li>
        <li>Any legal issues arising from copyright infringement by users</li>
        <li>Viruses, malware, or security issues from downloaded content</li>
      </ul>
      <p>
        <strong>Use at Your Own Risk:</strong> You acknowledge and agree that you use our YouTube and TikTok video downloader service entirely at your own risk.
      </p>
    </div>

    <div className="disclaimer-block">
      <h2>Jurisdictional Issues</h2>
      <p>
        <strong>International Service:</strong> Our service is accessible worldwide, but laws regarding video downloading vary by country and region.
      </p>
      <p>
        <strong>Your Responsibility:</strong> It is your responsibility to ensure that using our service complies with the laws of your country, state, or region. If downloading videos is illegal in your jurisdiction, you should not use our service.
      </p>
    </div>

    <div className="disclaimer-block">
      <h2>Changes to This Disclaimer</h2>
      <p>
        We reserve the right to update, modify, or change this disclaimer at any time without prior notice. Changes will be effective immediately upon posting to this page.
      </p>
      <p>
        <strong>Your Continued Use:</strong> Your continued use of our YouTube and TikTok video downloader service after any changes to this disclaimer constitutes your acceptance of the updated terms.
      </p>
      <p>
        <strong>Check Regularly:</strong> We encourage you to review this disclaimer periodically to stay informed about how we operate and your responsibilities as a user.
      </p>
    </div>

    <div className="disclaimer-block contact-block">
      <h2>Questions or Concerns?</h2>
      <p>
        If you have any questions about this disclaimer or our YouTube and TikTok video downloader service, please contact us:
      </p>
      <p><strong>Email:</strong> ramsis.nagham@outlook.com</p>
      <p>
        We will do our best to respond to your inquiries in a timely manner.
      </p>
    </div>

    <div className="disclaimer-cta">
      <h2>By Using Our Service, You Accept This Disclaimer</h2>
      <p>If you do not agree with any part of this disclaimer, please do not use our YouTube and TikTok video downloader service.</p>
      <a href="/" className="cta-button">I Understand - Start Downloading</a>
    </div>
  </>
);

export default Disclaimer;
